/**
 * Agent SSE Service
 *
 * 基于 edu-web-chat-engine 的成熟 SSE 实现
 * 管理 Agent 实例的生命周期和 SSE 消息流
 */

import { allocateInstance, sendMessage as apiSendMessage, ALLOCATOR_BASE_URL } from '@/lib/api/agent';

// ── 日志工具 ──

const createLogger = (prefix: string) => ({
	info: (...args: unknown[]) => console.log(`[${prefix}]`, ...args),
	debug: (...args: unknown[]) => console.log(`[${prefix}]`, ...args),
	warn: (...args: unknown[]) => console.warn(`[${prefix}]`, ...args),
	error: (...args: unknown[]) => console.error(`[${prefix}]`, ...args),
});

const log = createLogger('agent-sse');

// ── SSE 消息类型 ──

export interface AgentUsage {
	input_tokens: number;
	output_tokens: number;
	cache_creation_input_tokens?: number;
	cache_read_input_tokens?: number;
}

export interface AgentResult {
	duration_ms: number;
	duration_api_ms: number;
	total_cost_usd: number;
	usage: AgentUsage;
	stop_reason: string;
	result: string;
}

export interface AgentSystemInfo {
	model: string;
	session_id: string;
	tools: string[];
	claude_code_version: string;
}

export interface ClaudeCodeMessage {
	type:
		| 'assistant'
		| 'user'
		| 'result'
		| 'query_start'
		| 'query_stop'
		| 'system'
		| 'my_custom_message'
		| 'stream_event'
		| 'tool_progress'
		| 'auth_status'
		| 'tool_use_summary'
		| 'rate_limit_event'
		| 'prompt_suggestion';
	queryId?: string;
	action?: string;
	name?: string;
	file_path?: string;
	event?: string;
	message?: {
		content: Array<{
			type: string;
			id?: string;
			name?: string;
			text?: string;
			thinking?: string;
			input?: Record<string, unknown>;
			description?: string;
			tool_use_id?: string;
			content?: string | unknown;
		}>;
		usage?: AgentUsage;
	};
	result?: string;
	duration_ms?: number;
	duration_api_ms?: number;
	total_cost_usd?: number;
	usage?: AgentUsage;
	stop_reason?: string;
	model?: string;
	session_id?: string;
	tools?: string[];
	claude_code_version?: string;
	subtype?: string;
}

export interface AgentToolUse {
	id: string;
	name: string;
	input: Record<string, unknown>;
	description?: string;
}

export interface AgentToolResult {
	toolUseId: string;
	content: string;
}

export interface AgentFileChanged {
	filePath: string;
	event: string;
}

// ── Agent SSE Service ──

export class AgentSseService {
	private instanceId: string | null = null;
	private eventSource: EventSource | null = null;
	private messageCallback: ((content: string) => void) | null = null;
	private userMessageCallback: ((content: string) => void) | null = null;
	private thinkingCallback: ((thinking: string) => void) | null = null;
	private resultCallback: ((result: AgentResult) => void) | null = null;
	private systemCallback: ((info: AgentSystemInfo) => void) | null = null;
	private toolUseCallback: ((tool: AgentToolUse) => void) | null = null;
	private toolResultCallback: ((result: AgentToolResult) => void) | null = null;
	private endCallback: (() => void) | null = null;
	private errorCallback: ((error: Error) => void) | null = null;
	private fileChangedCallback: ((info: AgentFileChanged) => void) | null = null;
	private queryStartCallback: (() => void) | null = null;

	/** 重连相关 */
	private reconnectAttempts = 0;
	private readonly maxReconnectAttempts = 5;
	private readonly reconnectDelays = [1000, 2000, 4000, 8000, 16000]; // 退避延迟
	private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	private lastListenOptions: Parameters<AgentSseService['startListening']> | null = null;
	private stopped = false; // 主动停止时不重连
	private lastConnectTime = 0; // 上次连接成功时间，用于检测快速断连
	private stabilityTimer: ReturnType<typeof setTimeout> | null = null; // 连接稳定后重置重连计数
	private readonly stableThresholdMs = 30_000; // 连接保持 30s 认为稳定

	/**
	 * 分配新实例
	 */
	async allocate(): Promise<string> {
		log.info('创建工作区');
		const result = await allocateInstance();
		this.instanceId = result.instanceId;
		log.info('工作区创建成功', { instanceId: result.instanceId });
		return result.instanceId;
	}

	/**
	 * 获取当前实例 ID
	 */
	getInstanceId(): string | null {
		return this.instanceId;
	}

	/**
	 * 设置实例 ID（用于恢复会话）
	 */
	setInstanceId(id: string) {
		this.instanceId = id;
		log.info('设置实例 ID', { instanceId: id });
	}

	/**
	 * 发送消息到当前实例
	 */
	async send(message: string): Promise<void> {
		if (!this.instanceId) {
			throw new Error('未分配实例');
		}

		log.info('发送消息', { instanceId: this.instanceId, message: message.slice(0, 50) });
		await apiSendMessage({
			instanceId: this.instanceId,
			message,
		});
		log.info('消息发送成功');
	}

	/**
	 * 开始监听消息流（SSE）
	 *
	 * 基于 edu-web-chat-engine 的实现，支持：
	 * - 自动认证参数传递
	 * - 开发/生产环境自动切换
	 * - 自定义事件类型
	 * - 详细日志记录
	 * - 完整解析 Claude Code 消息格式（文本、思考、使用统计、系统信息）
	 */
	startListening(
		onMessage: (content: string) => void,
		options?: {
			history?: boolean;
			onUserMessage?: (content: string) => void;
			onThinking?: (thinking: string) => void;
			onResult?: (result: AgentResult) => void;
			onSystem?: (info: AgentSystemInfo) => void;
			onToolUse?: (tool: AgentToolUse) => void;
			onToolResult?: (result: AgentToolResult) => void;
			onFileChanged?: (info: AgentFileChanged) => void;
			onQueryStart?: () => void;
			onEnd?: () => void;
			onError?: (error: Error) => void;
		},
	): void {
		if (!this.instanceId) {
			throw new Error('未分配实例');
		}

		// 关闭之前的连接（不触发重连）
		this.stopped = true;
		this.stopListening();
		this.stopped = false;
		this.reconnectAttempts = 0;

		this.lastListenOptions = [onMessage, options];

		this.messageCallback = onMessage;
		this.userMessageCallback = options?.onUserMessage || null;
		this.thinkingCallback = options?.onThinking || null;
		this.resultCallback = options?.onResult || null;
		this.systemCallback = options?.onSystem || null;
		this.toolUseCallback = options?.onToolUse || null;
		this.toolResultCallback = options?.onToolResult || null;
		this.fileChangedCallback = options?.onFileChanged || null;
		this.queryStartCallback = options?.onQueryStart || null;
		this.endCallback = options?.onEnd || null;
		this.errorCallback = options?.onError || null;

		// 构建 SSE URL

		const history = options?.history ?? false;

		const params = new URLSearchParams({
			instanceId: this.instanceId,
			history: String(history),
		});

		const url = `${ALLOCATOR_BASE_URL}/message.read?${params.toString()}`;

		log.info('SSE 建立连接', { url, instanceId: this.instanceId });

		try {
			this.eventSource = new EventSource(url);

			let lastActivityTime = Date.now();

			this.eventSource.onopen = () => {
				lastActivityTime = Date.now();
				this.lastConnectTime = Date.now();
				this.scheduleStabilityReset();
				log.info('SSE 连接已建立');
			};

			this.eventSource.onmessage = (event) => {
				lastActivityTime = Date.now();
				try {
					if (!event.data) return;
					this.handleSseMessage(event.data);
				} catch (error) {
					log.error('SSE 解析消息失败', error);
					this.errorCallback?.(error instanceof Error ? error : new Error('Failed to parse SSE message'));
				}
			};

			this.eventSource.onerror = () => {
				const idleSeconds = ((Date.now() - lastActivityTime) / 1000).toFixed(1);
				log.warn('SSE 连接断开', { idleSeconds: `${idleSeconds}s`, attempt: this.reconnectAttempts });

				this.cancelStabilityReset();

				// 关闭当前连接但不清理回调
				if (this.eventSource) {
					this.eventSource.close();
					this.eventSource = null;
				}

				// 尝试重连
				if (!this.stopped && this.reconnectAttempts < this.maxReconnectAttempts) {
					const delay = this.reconnectDelays[this.reconnectAttempts] ?? 4000;
					this.reconnectAttempts++;
					log.info(`SSE ${delay}ms 后重连 (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
					this.reconnectTimer = setTimeout(() => {
						this.reconnectTimer = null;
						if (this.stopped || !this.lastListenOptions) return;
						log.info('SSE 重连中...');
						this.reconnect();
					}, delay);
				} else if (!this.stopped) {
					// 重连耗尽 → 通知上层
					log.error('SSE 重连失败，已达最大重试次数');
					this.errorCallback?.(new Error(`SSE 连接中断 (空闲 ${idleSeconds}s，重连 ${this.maxReconnectAttempts} 次失败)`));
					this.cleanupCallbacks();
				}
			};
		} catch (error) {
			log.error('创建 SSE 连接失败', error);
			this.errorCallback?.(error instanceof Error ? error : new Error('Failed to create SSE connection'));
		}
	}

	/**
	 * 解析并分发 SSE 消息（startListening / reconnect 共用）
	 */
	private handleSseMessage(raw: string): void {
		log.debug('SSE 收到消息', { data: raw });

		let data: ClaudeCodeMessage = JSON.parse(raw);

		// 处理自定义消息类型
		while (data.type === 'my_custom_message') {
			if (data.action === 'file_changed') {
				log.debug(`文件变更事件 ${data.file_path}`);
				this.fileChangedCallback?.({ filePath: data.file_path ?? '', event: data.event ?? '' });
			} else if (data.action === 'event_hook') {
				log.debug(`其他一般事件 ${data.name}`);
			} else if (data.action === 'post_tool_use') {
				log.debug(`工具 "${data.name}" 使用结束`);
			} else if (data.action === 'query_start') {
				log.debug('SSE 查询开始 (custom)', { queryId: data.queryId });
				this.queryStartCallback?.();
			} else if (data.action === 'history') {
				const role = (data as unknown as { role: string }).role;
				const innerMessage = data.message;
				data = { type: role, message: innerMessage } as unknown as ClaudeCodeMessage;
				log.debug('history 消息解包', { role, contentItems: innerMessage?.content?.length });
				break;
			} else {
				log.warn('未处理的自定义消息', { data });
			}
			return;
		}

		if (data.type === 'assistant' && data.message?.content) {
			if (!Array.isArray(data.message.content)) {
				log.debug('SSE assistant content 非数组，跳过', { type: typeof data.message.content });
				return;
			}
			for (const item of data.message.content) {
				if (item.type === 'text' && item.text) {
					log.debug('SSE 文本消息', { text: item.text.slice(0, 100) });
					this.messageCallback?.(item.text);
				} else if (item.type === 'thinking' && item.thinking) {
					log.debug('SSE 思考消息', { thinking: item.thinking.slice(0, 100) });
					this.thinkingCallback?.(item.thinking);
				} else if (item.type === 'tool_use' && item.id && item.name) {
					log.debug('SSE 工具调用', { id: item.id, name: item.name, description: item.description, input: item.input });
					this.toolUseCallback?.({
						id: item.id,
						name: item.name,
						input: (item.input ?? {}) as Record<string, unknown>,
						description: item.description,
					});
				}
			}
		} else if (data.type === 'user' && data.message?.content) {
			if (!Array.isArray(data.message.content)) {
				log.debug('SSE user content 非数组，跳过', { type: typeof data.message.content });
				return;
			}
			const items = data.message.content;
			const hasToolResult = items.some((it) => it.type === 'tool_result');

			for (const item of items) {
				if (item.type === 'text' && item.text) {
					// 含有 tool_result 的 user 消息是系统 context，跳过其中的 text
					if (hasToolResult) {
						log.debug('SSE 跳过系统注入文本', { text: item.text.slice(0, 60) });
						continue;
					}
					// 过滤明显的系统注入内容（skill 加载、长文本等）
					if (item.text.length > 500 || item.text.startsWith('Base directory for this skill')) {
						log.debug('SSE 跳过系统注入文本(长)', { len: item.text.length });
						continue;
					}
					log.debug('SSE 用户文本消息', { text: item.text.slice(0, 100) });
					this.userMessageCallback?.(item.text);
				} else if (item.type === 'tool_result' && item.tool_use_id) {
					log.debug('SSE 工具结果', { toolUseId: item.tool_use_id });
					const content = typeof item.content === 'string' ? item.content : JSON.stringify(item.content);
					this.toolResultCallback?.({
						toolUseId: item.tool_use_id,
						content,
					});
				}
			}
		} else if (data.type === 'result') {
			log.debug('SSE 结果消息', {
				duration: data.duration_ms,
				cost: data.total_cost_usd,
				tokens: data.usage,
			});
			if (this.resultCallback && data.result) {
				this.resultCallback({
					duration_ms: data.duration_ms || 0,
					duration_api_ms: data.duration_api_ms || 0,
					total_cost_usd: data.total_cost_usd || 0,
					usage: data.usage || { input_tokens: 0, output_tokens: 0 },
					stop_reason: data.stop_reason || 'unknown',
					result: data.result,
				});
			}
		} else if (data.type === 'system' && data.subtype === 'init') {
			log.debug('SSE 系统消息', {
				model: data.model,
				session_id: data.session_id,
				tools_count: data.tools?.length,
			});
			if (this.systemCallback && data.model && data.session_id) {
				this.systemCallback({
					model: data.model,
					session_id: data.session_id,
					tools: data.tools || [],
					claude_code_version: data.claude_code_version || 'unknown',
				});
			}
		} else if (data.type === 'query_stop') {
			log.info('SSE 查询结束');
			this.endCallback?.();
		} else if (data.type === 'system') {
			log.debug('SSE 系统消息', { subtype: data.subtype });
		} else if (data.type === 'query_start') {
			log.debug('SSE 查询开始', { queryId: data.queryId });
			this.queryStartCallback?.();
		}
	}

	/**
	 * 当前是否有活跃的 SSE 连接
	 */
	isListening(): boolean {
		return this.eventSource !== null && this.eventSource.readyState !== EventSource.CLOSED;
	}

	/**
	 * 内部重连：复用已保存的回调，只重建 EventSource
	 */
	private reconnect(): void {
		if (!this.instanceId || !this.lastListenOptions) return;

		const history = false; // 重连不需要历史
		const params = new URLSearchParams({
			instanceId: this.instanceId,
			history: String(history),
		});
		const url = `${ALLOCATOR_BASE_URL}/message.read?${params.toString()}`;
		log.info('SSE 重连建立连接', { url, attempt: this.reconnectAttempts });

		try {
			this.eventSource = new EventSource(url);

			let lastActivityTime = Date.now();

			this.eventSource.onopen = () => {
				lastActivityTime = Date.now();
				this.lastConnectTime = Date.now();
				this.scheduleStabilityReset();
				log.info('SSE 重连成功');
			};

			this.eventSource.onmessage = (event) => {
				lastActivityTime = Date.now();
				try {
					if (!event.data) return;
					this.handleSseMessage(event.data);
				} catch (error) {
					log.error('SSE 解析消息失败', error);
					this.errorCallback?.(error instanceof Error ? error : new Error('Failed to parse SSE message'));
				}
			};

			this.eventSource.onerror = () => {
				const idleSeconds = ((Date.now() - lastActivityTime) / 1000).toFixed(1);
				log.warn('SSE 连接断开', { idleSeconds: `${idleSeconds}s`, attempt: this.reconnectAttempts });

				this.cancelStabilityReset();

				if (this.eventSource) {
					this.eventSource.close();
					this.eventSource = null;
				}
				if (!this.stopped && this.reconnectAttempts < this.maxReconnectAttempts) {
					const delay = this.reconnectDelays[this.reconnectAttempts] ?? 4000;
					this.reconnectAttempts++;
					log.info(`SSE ${delay}ms 后重连 (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
					this.reconnectTimer = setTimeout(() => {
						this.reconnectTimer = null;
						if (this.stopped) return;
						this.reconnect();
					}, delay);
				} else if (!this.stopped) {
					log.error('SSE 重连失败，已达最大重试次数');
					this.errorCallback?.(new Error(`SSE 连接中断 (空闲 ${idleSeconds}s，重连 ${this.maxReconnectAttempts} 次失败)`));
					this.cleanupCallbacks();
				}
			};
		} catch (error) {
			log.error('SSE 重连失败', error);
			this.errorCallback?.(error instanceof Error ? error : new Error('SSE reconnect failed'));
			this.cleanupCallbacks();
		}
	}

	private scheduleStabilityReset(): void {
		this.cancelStabilityReset();
		this.stabilityTimer = setTimeout(() => {
			this.stabilityTimer = null;
			if (this.reconnectAttempts > 0) {
				log.info('SSE 连接稳定 30s，重置重连计数');
				this.reconnectAttempts = 0;
			}
		}, this.stableThresholdMs);
	}

	private cancelStabilityReset(): void {
		if (this.stabilityTimer) {
			clearTimeout(this.stabilityTimer);
			this.stabilityTimer = null;
		}
	}

	private cleanupCallbacks(): void {
		this.messageCallback = null;
		this.userMessageCallback = null;
		this.thinkingCallback = null;
		this.resultCallback = null;
		this.systemCallback = null;
		this.toolUseCallback = null;
		this.toolResultCallback = null;
		this.fileChangedCallback = null;
		this.queryStartCallback = null;
		this.endCallback = null;
		this.errorCallback = null;
		this.lastListenOptions = null;
	}

	/**
	 * 停止监听消息流
	 */
	stopListening(): void {
		this.stopped = true;
		this.cancelStabilityReset();
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
		if (this.eventSource) {
			log.info('关闭 SSE 连接');
			this.eventSource.close();
			this.eventSource = null;
		}
		this.cleanupCallbacks();
	}

	/**
	 * 清理资源
	 */
	cleanup(): void {
		this.stopListening();
		this.instanceId = null;
		log.info('清理 Agent 服务');
	}
}

// 单例实例
export const agentSseService = new AgentSseService();
